// Helpers the controller and the display share for keeping their view of the
// live game in step with the server's stage records. Synced to each app's
// src/shared/ by scripts/sync-shared.js; edit this copy, then run
// `npm run sync:shared`.
//
// The server stamps every stage with the sequencer time it began
// (`metadata.timelineTimestamp` on a live event, `emittedAt` on a replayed
// record) and says how long it lasts. That is enough to tell an older replay
// from a newer stage, and to notice a stage that should have ended a while ago
// with nothing newer arriving: a client that is connected but no longer
// hearing the game.

// How long past its published end a stage may run before the client asks for
// the current stage again. Covers network delay and a busy server; a stage
// that is merely late arrives well inside it.
export const STAGE_STALL_GRACE_MS = 8000;
// Spread so clients that all see the same late stage do not ask at once.
export const STAGE_STALL_JITTER_MS = 3000;
// How long a resync request may go unanswered before the connection itself is
// presumed dead and rebuilt.
export const STAGE_RESYNC_RESPONSE_TIMEOUT_MS = 8000;
export const STAGE_RESYNC_RETRY_BASE_MS = 15000;
export const STAGE_RESYNC_RETRY_MAX_MS = 60000;
// Asking again without anything changing stops here until a new stage arrives.
export const STAGE_RESYNC_MAX_ATTEMPTS = 4;
// Checks the client could not act on (offline, or another re-sync already
// asking) are not attempts; they look again at the base interval, this many
// times in a row at most.
export const STAGE_STALL_MAX_SKIPPED_CHECKS = 20;

// Stages that end when something else happens - an operator, or the next
// game's preparation - rather than on a timer, so they are never overdue.
const OPEN_ENDED_STAGE_TYPES = new Set([
  'game-over',
  'game-stopped',
  'game-cycle-complete',
  'timeline-started',
  'timeline-stopped',
]);

// Replaced by the next stage in the same server tick.
const INSTANT_STAGE_TYPES = new Set(['ad-break-end']);

// Module-scoped frozen fallback to eliminate `{}` object allocations when metadata is missing.
const EMPTY_METADATA = Object.freeze({});

const toFiniteNumber = (value) => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

export function resolveStageType(stage) {
  if (!stage || typeof stage !== 'object') return null;
  return stage.type || stage.eventType || stage.payload?.eventType || null;
}

export function resolveStageMetadata(stage) {
  if (!stage || typeof stage !== 'object') return EMPTY_METADATA;
  return stage.metadata || stage.payload?.metadata || EMPTY_METADATA;
}

// When the stage began on the server's timeline, or null if it does not say.
export function resolveStageTimelineStamp(stage) {
  if (!stage || typeof stage !== 'object') return null;
  return (
    toFiniteNumber(stage.emittedAt)
    ?? toFiniteNumber(stage.metadata?.timelineTimestamp)
    ?? toFiniteNumber(stage.payload?.metadata?.timelineTimestamp)
  );
}

// True only when both are known and the stage began before the one already
// applied. Equal stamps are the same stage seen twice, which is not older.
export function isStageOlderThan(stage, appliedStamp) {
  const stamp = resolveStageTimelineStamp(stage);
  const applied = toFiniteNumber(appliedStamp);
  return stamp !== null && applied !== null && stamp < applied;
}

// How long the stage has left as of when it was sent, or null for a stage
// with no fixed end.
export function resolveStageRemainingMs(stage) {
  const type = resolveStageType(stage);
  if (!type || OPEN_ENDED_STAGE_TYPES.has(type)) return null;
  if (INSTANT_STAGE_TYPES.has(type)) return 0;
  const payload = stage.payload || {};
  const remaining = toFiniteNumber(stage.remainingMs) ?? toFiniteNumber(payload.remainingMs);
  if (remaining !== null) return Math.max(0, remaining);
  const durationMs = toFiniteNumber(stage.durationMs) ?? toFiniteNumber(payload.durationMs);
  if (durationMs !== null) return Math.max(0, durationMs);
  const duration = toFiniteNumber(stage.duration) ?? toFiniteNumber(payload.duration);
  if (duration === null) return null;
  // An ad break's `duration` is in seconds; every other stage's is in ms.
  return Math.max(0, type === 'start-ad-break' ? duration * 1000 : duration);
}

// Identifies one occurrence of a stage. Two messages with the same key are the
// same stage (a live event and its snapshot, or a replay of it).
export function buildStageKey(stage) {
  const type = resolveStageType(stage);
  if (!type) return null;
  const metadata = resolveStageMetadata(stage);
  const stamp = resolveStageTimelineStamp(stage);
  // Optimization: Direct template string interpolation bypasses 5-element array allocations
  // and `.join('|')` call overhead in high-frequency stage tracking loops (~3.4x speedup).
  return `${stage.gameId ?? ''}|${type}|${stamp ?? ''}|${metadata.roundNumber ?? ''}|${metadata.questionNumber ?? ''}`;
}

export function summarizeStage(stage) {
  const metadata = resolveStageMetadata(stage);
  return {
    gameId: stage?.gameId ?? null,
    stageType: resolveStageType(stage),
    roundNumber: metadata.roundNumber ?? null,
    questionNumber: metadata.questionNumber ?? null,
    timelineTimestamp: resolveStageTimelineStamp(stage),
  };
}

// Watches the client's current stage and calls `onStall` once it is overdue
// with nothing newer arriving. `onStall` returns false when it did not ask the
// server (offline, a join already in flight); that check is not an attempt and
// is retried later, a bounded number of times. Otherwise, if no response is
// noted within the timeout, `onResyncTimeout` is called so the client can
// rebuild its connection. Further checks back off and stop after a bounded
// number of attempts until a new stage arrives. `expectResponse` applies the
// same timeout to a request the client made on its own (a resume re-sync). It
// never changes game state: the client recovers through whatever its normal
// handlers do with the server's answer.
export function createStageStallMonitor({
  onStall,
  onResyncTimeout,
  onRecovered,
  now = () => Date.now(),
  setTimer = (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimer = (handle) => clearTimeout(handle),
  random = Math.random,
  graceMs = STAGE_STALL_GRACE_MS,
  jitterMs = STAGE_STALL_JITTER_MS,
  responseTimeoutMs = STAGE_RESYNC_RESPONSE_TIMEOUT_MS,
  retryBaseMs = STAGE_RESYNC_RETRY_BASE_MS,
  retryMaxMs = STAGE_RESYNC_RETRY_MAX_MS,
  maxAttempts = STAGE_RESYNC_MAX_ATTEMPTS,
  maxSkippedChecks = STAGE_STALL_MAX_SKIPPED_CHECKS,
} = {}) {
  let current = null;
  let attempts = 0;
  let skippedChecks = 0;
  let checkTimer = null;
  let responseTimer = null;

  const clearCheck = () => {
    if (checkTimer === null) return;
    clearTimer(checkTimer);
    checkTimer = null;
  };
  const clearResponse = () => {
    if (responseTimer === null) return;
    clearTimer(responseTimer);
    responseTimer = null;
  };
  const scheduleCheck = (delayMs) => {
    clearCheck();
    checkTimer = setTimer(runCheck, Math.max(0, delayMs));
  };
  const firstCheckDelay = (remainingMs) => (
    remainingMs + graceMs + Math.floor(Math.max(0, random()) * jitterMs)
  );

  function armResponseTimeout(detail) {
    clearResponse();
    responseTimer = setTimer(() => {
      responseTimer = null;
      if (onResyncTimeout) onResyncTimeout(detail);
    }, responseTimeoutMs);
  }

  function runCheck() {
    checkTimer = null;
    if (!current || current.deadline === null || attempts >= maxAttempts) return;
    const detail = {
      ...current.summary,
      overdueMs: Math.max(0, now() - current.deadline),
      attempt: attempts + 1,
    };
    const asked = onStall ? onStall(detail) : false;
    if (asked === false) {
      // Nothing was asked, so this was not an attempt. Spending attempts here
      // let a client hidden behind a resume curtain use them all up and then
      // never rebuild a dead connection once it came back.
      skippedChecks += 1;
      if (skippedChecks < maxSkippedChecks) scheduleCheck(retryBaseMs);
      return;
    }
    attempts += 1;
    skippedChecks = 0;
    armResponseTimeout(detail);
    scheduleCheck(Math.min(retryMaxMs, retryBaseMs * (2 ** (attempts - 1))));
  }

  // Any message from the server shows the connection is alive.
  function noteResponse() {
    clearResponse();
  }

  // The client has asked the server something on its own (a resume re-sync)
  // over a connection that says it is open. If nothing at all answers, the
  // connection is dead and is rebuilt, exactly as for an unanswered resync.
  function expectResponse(detail = {}) {
    if (responseTimer !== null) return;
    armResponseTimeout({ ...(current ? current.summary : {}), ...detail });
  }

  function noteStage(stage) {
    noteResponse();
    const key = stage ? buildStageKey(stage) : null;
    if (!key) {
      // Nothing running: nothing can be overdue.
      reset();
      return;
    }
    const remainingMs = resolveStageRemainingMs(stage);
    if (current && current.key === key) {
      // The same stage again. A replay that says it legitimately has time left
      // moves the deadline later; one that is as overdue as we are (the
      // server itself has not moved on) leaves the back-off running, and
      // nothing ever moves the deadline earlier.
      if (remainingMs !== null && remainingMs > 0 && current.deadline !== null) {
        const deadline = now() + remainingMs;
        if (deadline > current.deadline + 1000) {
          current.deadline = deadline;
          attempts = 0;
          skippedChecks = 0;
          scheduleCheck(firstCheckDelay(remainingMs));
        }
      }
      return;
    }
    const previous = current;
    const wasStalled = attempts > 0;
    current = {
      key,
      summary: summarizeStage(stage),
      deadline: remainingMs === null ? null : now() + remainingMs,
    };
    attempts = 0;
    skippedChecks = 0;
    clearCheck();
    if (wasStalled && previous && onRecovered) {
      onRecovered({ from: previous.summary, to: current.summary });
    }
    if (current.deadline !== null) {
      scheduleCheck(firstCheckDelay(remainingMs));
    }
  }

  // Forget the current stage, e.g. on a disconnect: the reconnect path
  // rejoins and its answer starts a fresh watch.
  function reset() {
    clearCheck();
    clearResponse();
    current = null;
    attempts = 0;
    skippedChecks = 0;
  }

  return {
    noteStage,
    noteResponse,
    expectResponse,
    reset,
    getState: () => ({
      stage: current ? { ...current.summary } : null,
      deadline: current ? current.deadline : null,
      attempts,
    }),
  };
}
